"use client";

import { createPortal } from "react-dom";
import { useEffect, useState, type RefObject } from "react";

type Side = "top" | "bottom";

type Props = {
  anchorRef: RefObject<HTMLElement | null>;
  show: boolean;
  active?: boolean;
  label: string;
  side?: Side;
  /** Gap in px between anchor edge and badge. */
  gap?: number;
};

/**
 * Hover badge for keyboard-modifier shortcuts. Portalled into `document.body`
 * so it clears sibling React Flow nodes whatever their stacking order.
 */
export function KeystrokeHint({
  anchorRef,
  show,
  active,
  label,
  side = "bottom",
  gap = 8,
}: Props) {
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);

  useEffect(() => {
    if (!show) {
      setPos(null);
      return;
    }
    const el = anchorRef.current;
    if (!el) return;
    let raf = 0;
    let cancelled = false;
    let lastX = NaN;
    let lastY = NaN;
    const update = () => {
      if (cancelled) return;
      const r = el.getBoundingClientRect();
      const x = r.left + r.width / 2;
      const y = side === "bottom" ? r.bottom + gap : r.top - gap;
      if (x !== lastX || y !== lastY) {
        lastX = x;
        lastY = y;
        setPos({ x, y });
      }
      raf = requestAnimationFrame(update);
    };
    raf = requestAnimationFrame(update);
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
    };
  }, [show, anchorRef, side, gap]);

  if (!show || !pos) return null;

  return createPortal(
    <div
      style={{
        position: "fixed",
        left: pos.x,
        top: pos.y,
        transform: side === "bottom" ? "translateX(-50%)" : "translate(-50%, -100%)",
        pointerEvents: "none",
        whiteSpace: "nowrap",
        padding: "2px 6px",
        borderRadius: 5,
        fontSize: 9.5,
        fontWeight: 600,
        letterSpacing: "0.04em",
        textTransform: "uppercase",
        background: active ? "var(--color-accent)" : "var(--color-surface)",
        color: active ? "white" : "var(--color-fg-muted)",
        border: active ? "none" : "1px solid var(--color-border)",
        boxShadow: "var(--shadow-xs)",
        zIndex: 9999,
      }}
    >
      {label}
    </div>,
    document.body,
  );
}

const isMac =
  typeof navigator !== "undefined" && /Mac|iPhone|iPad|iPod/.test(navigator.platform);

export const MOD_LABEL = isMac ? "⌘" : "ctrl";
export const ALT_LABEL = isMac ? "⌥" : "alt";
export const SHIFT_LABEL = "⇧";
